import React from "react";
import { CopyOutlined, DeleteOutlined, DownOutlined, EditOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { Dropdown } from "antd5";
import classNames from "classnames";

import Button from "components/ui/button/Button";
import Tooltip from "components/ui/tooltip/Tooltip";
import {
  DeleteListModal,
  DuplicateListModal,
  RenameListModal,
} from "components/user_lists/UserListsModals";
import { ALWAYS_SHOW, SHOW_ON_HOVER } from "lib/core_components/ShowOnHover";
import { NoticeListSummary } from "lib/hooks/api/notices/lists/useNoticeLists";
import { useDialogManager } from "lib/providers/DialogManager";
import { EventDataTypes, TrackingProvider, useTracking } from "lib/tracking";

type UserListProps = {
  list: NoticeListSummary;
};

export function EditListMenu({ list }: UserListProps) {
  const dialogManager = useDialogManager();
  const { trackingData } = useTracking();
  const canEdit = list.isOwner;
  const [dropdownOpen, setDropdownOpen] = React.useState(false);

  return (
    <TrackingProvider
      data={{ "List name": list.name, "List id": list.id, "Data type": EventDataTypes.recordList }}
    >
      <Dropdown
        className={classNames(SHOW_ON_HOVER, dropdownOpen && ALWAYS_SHOW)}
        onOpenChange={(menuHoveredOver) => setDropdownOpen(menuHoveredOver)}
        open={dropdownOpen}
        placement="bottomRight"
        menu={{
          items: [
            {
              label: "Duplicate",
              icon: <CopyOutlined />,
              key: "duplicate",
            },
            {
              label: (
                <Tooltip
                  content={
                    canEdit
                      ? undefined
                      : "You can’t rename this list because you are not the owner."
                  }
                >
                  <span>Rename</span>
                </Tooltip>
              ),
              icon: <EditOutlined />,
              key: "rename",
              disabled: !canEdit,
            },
            {
              label: (
                <Tooltip
                  content={
                    canEdit
                      ? undefined
                      : "You can’t delete this list because you are not the owner."
                  }
                >
                  <span>Delete</span>
                </Tooltip>
              ),
              icon: <DeleteOutlined />,
              key: "delete",
              disabled: !canEdit,
            },
          ],
          onClick: (e) => {
            e.domEvent.stopPropagation();
            if (e.key === "duplicate") {
              return void dialogManager.openDialog(DuplicateListModal, {
                list: list,
                trackingData,
              });
            }
            if (e.key === "rename") {
              return void dialogManager.openDialog(RenameListModal, {
                list: list,
                trackingData,
              });
            }
            if (e.key === "delete") {
              return void dialogManager.openDialog(DeleteListModal, {
                list: list,
                trackingData,
              });
            }
          },
        }}
      >
        <Button buttonType="secondary">
          <ActionsDropdownText>Actions</ActionsDropdownText>
          <DownOutlined />
        </Button>
      </Dropdown>
    </TrackingProvider>
  );
}

const ActionsDropdownText = styled.span(() => ({
  paddingRight: "4px",
}));
